// -----------------------------------------------------------
// FloraIQ - OperationalOverview (Aba Visão Geral)
// -----------------------------------------------------------

import { useState } from "react";
import { timelineStages, overviewProgressByAction, stagesByStatus, blockingIssues } from "@/mocks/operational";
import StageStatusBadge from "./StageStatusBadge";
import StageDetailDrawer from "./StageDetailDrawer";
import { planningChecklist, planningPendencias } from "@/mocks/operational";

export default function OperationalOverview() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedStageIdx, setSelectedStageIdx] = useState(0);

  const stageChecklists = [planningChecklist, [], [], [], [], [], []];
  const stagePendencias = [planningPendencias, [], [], [], [], [], []];

  return (
    <div className="space-y-5">
      {/* Timeline horizontal */}
      <div>
        <h4 className="text-sm font-bold text-foreground-900 mb-4">Timeline das Etapas</h4>
        <div className="relative">
          <div className="hidden md:flex items-start gap-0">
            {timelineStages.map((stage, idx) => (
              <div key={stage.id} className="flex-1 relative">
                {/* Connector */}
                {idx < timelineStages.length - 1 && (
                  <div className="absolute top-4 left-[calc(50%+16px)] right-0 h-1 bg-background-200 z-0">
                    <div
                      className="h-full bg-primary-500 transition-all duration-700 rounded-full"
                      style={{ width: `${stage.progresso}%` }}
                    ></div>
                  </div>
                )}
                <div className="flex flex-col items-center relative z-10">
                  <button
                    onClick={() => { setSelectedStageIdx(idx); setDrawerOpen(true); }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all cursor-pointer ${
                      stage.status === "concluida" ? "bg-primary-500 text-white" :
                      stage.status === "bloqueada" ? "bg-red-500 text-white" :
                      stage.status === "em_andamento" || stage.status === "em_revisao" ? "bg-accent-500 text-white" :
                      "bg-background-200 text-foreground-500"
                    }`}
                  >
                    {stage.status === "concluida" ? <i className="ri-check-line"></i> : stage.id}
                  </button>
                  <p className="text-[10px] font-medium text-foreground-700 mt-1.5 text-center leading-tight">{stage.nome}</p>
                  <StageStatusBadge status={stage.status} className="mt-1 scale-[0.85]" />
                  <span className="text-[10px] text-foreground-400 mt-0.5">{stage.progresso}%</span>
                </div>
              </div>
            ))}
          </div>
          {/* Mobile vertical */}
          <div className="md:hidden space-y-3">
            {timelineStages.map((stage, idx) => (
              <div key={stage.id} className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    stage.status === "concluida" ? "bg-primary-500 text-white" :
                    stage.status === "bloqueada" ? "bg-red-500 text-white" :
                    stage.status === "em_andamento" || stage.status === "em_revisao" ? "bg-accent-500 text-white" :
                    "bg-background-200 text-foreground-500"
                  }`}>
                    {stage.status === "concluida" ? <i className="ri-check-line"></i> : stage.id}
                  </div>
                  {idx < timelineStages.length - 1 && <div className="w-0.5 h-4 bg-background-200"></div>}
                </div>
                <button
                  onClick={() => { setSelectedStageIdx(idx); setDrawerOpen(true); }}
                  className="flex-1 text-left bg-background-50/70 rounded-lg p-2.5 border border-background-100/70 hover:border-primary-200 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-foreground-800">{stage.nome}</span>
                    <StageStatusBadge status={stage.status} className="scale-[0.85]" />
                  </div>
                  <div className="w-full bg-background-100 rounded-full h-1.5 mt-1.5">
                    <div className="bg-primary-500 h-1.5 rounded-full transition-all duration-700" style={{ width: `${stage.progresso}%` }}></div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Progresso por Ação */}
        <div className="rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-bold text-foreground-900 mb-3">Progresso por Ação</h4>
          <div className="space-y-3">
            {overviewProgressByAction.map((a) => (
              <div key={a.acao} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground-700 truncate mr-2">{a.acao}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`w-2 h-2 rounded-full ${a.status === "bloqueada" ? "bg-red-500" : a.status === "em_validacao" ? "bg-yellow-500" : "bg-accent-500"}`}></span>
                    <span className="text-xs font-bold text-foreground-800">{a.progresso}%</span>
                  </div>
                </div>
                <div className="w-full bg-background-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full transition-all duration-700 ${a.status === "bloqueada" ? "bg-red-500" : "bg-primary-500"}`} style={{ width: `${a.progresso}%` }}></div>
                </div>
                <p className="text-[10px] text-foreground-400">{a.etapa}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Etapas por Status */}
        <div className="rounded-card border border-background-200/70 p-4">
          <h4 className="text-sm font-bold text-foreground-900 mb-3">Etapas por Status</h4>
          <div className="space-y-2.5">
            {stagesByStatus.map((s) => (
              <div key={s.status} className="flex items-center gap-3">
                <span className={`w-3 h-3 rounded-sm shrink-0 ${s.cor}`}></span>
                <span className="text-xs text-foreground-700 flex-1">{s.status}</span>
                <span className="text-xs font-bold text-foreground-900">{s.quantidade}</span>
                <div className="w-24 bg-background-100 rounded-full h-1.5">
                  <div className={`h-1.5 rounded-full ${s.cor}`} style={{ width: `${Math.min((s.quantidade / 68) * 100, 100)}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Bloqueios */}
        <div className="rounded-card border border-background-200/70 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-red-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-red-600 text-sm"></i>
            </div>
            <h4 className="text-sm font-bold text-foreground-900">Bloqueios Críticos</h4>
            <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">{blockingIssues.length}</span>
          </div>
          <div className="space-y-2">
            {blockingIssues.map((b) => (
              <div key={b.id} className="flex items-start gap-2.5 p-2 rounded-lg bg-red-50/50 border border-red-100">
                <i className="ri-forbid-line text-red-500 text-sm mt-0.5 shrink-0"></i>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground-800 font-medium">{b.acao}</p>
                  <p className="text-xs text-foreground-500">{b.descricao}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] text-red-600">{b.responsavel}</span>
                    <span className="text-[10px] text-foreground-400">Prazo: {b.prazo}</span>
                  </div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                  b.severidade === "Crítica" ? "bg-red-100 text-red-700" : b.severidade === "Alta" ? "bg-orange-100 text-orange-700" : "bg-yellow-100 text-yellow-700"
                }`}>{b.severidade}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Próximos Passos */}
        <div className="rounded-card border border-background-200/70 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-arrow-right-circle-line text-accent-600 text-sm"></i>
            </div>
            <h4 className="text-sm font-bold text-foreground-900">Próximos Passos</h4>
          </div>
          <div className="space-y-2">
            {[
              { acao: "CTA Goiânia Centro", passo: "Concluir cadastro de campo no Setor Central", prazo: "30/06" },
              { acao: "Inventário Praça Universitária", passo: "Finalizar validação dos 2 registros pendentes", prazo: "05/07" },
              { acao: "Corredor Verde Sul", passo: "Concluir levantamento LiDAR do corredor", prazo: "15/07" },
              { acao: "Árvores Críticas Setor Bueno", passo: "Publicar mapa de risco para Defesa Civil", prazo: "10/07" },
              { acao: "Levantamento 3D Carbono Urbano", passo: "Resolver bloqueio do laser scanner", prazo: "15/07" },
            ].map((p, i) => (
              <div key={i} className="flex items-start gap-2.5 p-2 rounded-lg bg-background-50/70">
                <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-accent-600">{i + 1}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-foreground-700">{p.passo}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[10px] text-foreground-400">{p.acao}</span>
                    <span className="text-[10px] text-accent-600 font-medium">{p.prazo}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Qualidade Operacional */}
      <div className="rounded-card border border-background-200/70 p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-green-100 flex items-center justify-center">
            <i className="ri-check-double-line text-green-600 text-sm"></i>
          </div>
          <h4 className="text-sm font-bold text-foreground-900">Qualidade Operacional</h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Etapas no Prazo", valor: "38/42", pct: 90 },
            { label: "Completude de Dados", valor: "87%", pct: 87 },
            { label: "Cobertura de Área", valor: "98,7%", pct: 99 },
            { label: "Sincronização Campo", valor: "96%", pct: 96 },
          ].map((q) => (
            <div key={q.label} className="text-center">
              <div className="relative w-14 h-14 mx-auto mb-2">
                <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="24" fill="none" stroke="#E8EAED" strokeWidth="5" />
                  <circle cx="28" cy="28" r="24" fill="none" stroke={q.pct >= 90 ? "#1B6E4F" : q.pct >= 80 ? "#0052CC" : "#F57C00"} strokeWidth="5" strokeDasharray={`${q.pct * 1.51} 151`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-foreground-800">{q.pct}%</span>
              </div>
              <p className="text-xs text-foreground-500">{q.label}</p>
              <p className="text-[10px] text-foreground-400">{q.valor}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Drawer */}
      {timelineStages[selectedStageIdx] && (
        <StageDetailDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          stage={timelineStages[selectedStageIdx]}
          stageLabel={timelineStages[selectedStageIdx].nome}
          checklist={stageChecklists[selectedStageIdx] || []}
          pendencias={stagePendencias[selectedStageIdx] || []}
        />
      )}
    </div>
  );
}